<!--
@component
Menu that opens from a trigger button: actions, navigation, submenus, optional search filter and footer.

@use-instead-of custom absolutely-positioned menus
@figma https://www.figma.com/design/xb1BaksrwtGhe3JHzX1BbT/Design-System?node-id=5104-21663
@see DropdownItem, DropdownItemGroup, DropdownSubmenu, DropdownSelect
-->
<script lang="ts" module>
  export type DropdownContext = {
    focusedIndex: number;
    closeMenu: () => void;
    // Track the last navigation type used to focus an item.
    // This is used to prevent the "onmouseover" event from being triggered
    // when the user navs with keys.
    lastNavType: "key" | "mouse" | null;
  };
  export const DROPDOWN_CONTEXT = Symbol("dropdown-context");

  interface ChildrenProps {
    createSubmenu: (props: CreateDropdownSubmenuProps) => DropdownMenuSubmenu;
    closeMenu: () => void;
  }

  export interface FooterSegment {
    snippet: Snippet<[ChildrenProps]>;
    /** If `true`, render this snippet without a padded, bordered container. */
    raw?: boolean;
  }
</script>

<script lang="ts">
  import { browser } from "$app/environment";

  import {
    createDropdownMenu,
    type CreateDropdownSubmenuProps,
    createSync,
    type DropdownMenuSubmenu,
    melt,
  } from "@melt-ui/svelte";
  import type { Snippet } from "svelte";
  import { setContext, untrack } from "svelte";

  interface Props {
    align?: keyof typeof alignMap;
    gutter?: number;
    /** Renders the menu into the document body portal to escape overflow clipping and sit above overlays. */
    isPortalled?: boolean;
    closeOnInnerClick?: boolean;
    open?: boolean;
    button?: Snippet;
    children: Snippet<[ChildrenProps]>;
    // Rendered below the scrollable list and pinned to the bottom of the menu,
    // so it stays visible regardless of how far the list is scrolled.
    footer?: Snippet<[ChildrenProps]>;
    // In case multiple footer segments (with separators between) are desired.
    footerSegments?: FooterSegment[];
    filterPlaceholder?: string;
    onFilter?: (query: string) => void;
    // Needs to be passed if you want to support keyboard navigation of dropdown items.
    elements?: string[];
    contentMaxHeightPx?: number;
  }

  let {
    align = "left",
    gutter,
    isPortalled = false,
    closeOnInnerClick = true,
    open = $bindable(false),
    button,
    children,
    footer,
    footerSegments,
    filterPlaceholder,
    onFilter,
    elements,
    contentMaxHeightPx = 420,
  }: Props = $props();

  const allFooterSegments: FooterSegment[] = $derived([
    ...(footer ? [{ snippet: footer }] : []),
    ...(footerSegments ?? []),
  ]);

  let filterQuery = $state("");
  let isFilterable = $derived(!!onFilter);

  $effect(() => {
    if (onFilter) {
      onFilter(filterQuery);
    }
  });

  $effect(() => {
    if (!open && filterQuery) {
      filterQuery = "";
    }
  });

  const alignMap = {
    left: "bottom-start",
    right: "bottom-end",
    full: "bottom",

    topleft: "top-start",
    topright: "top-end",
    topfull: "top",

    leftstart: "left-start",
  } as const;

  let menuElement = $state<HTMLDivElement | null>(null);
  let triggerElement = $state<HTMLSpanElement | null>(null);

  // svelte-ignore state_referenced_locally
  const {
    elements: { menu, trigger },
    states,
    builders: { createSubmenu },
  } = createDropdownMenu({
    positioning: {
      placement: alignMap[align],
      // Prefer opening on the opposite side over shrinking into the viewport.
      flip: true,
      fitViewport: true,
      sameWidth: align === "full",
      ...(gutter !== undefined ? { gutter } : {}), // use default melt-ui gutter of 10px if none provided
    },
    preventScroll: false,
    // Determines which element to render the dropdown menu into.
    // undefined uses the parent portal; null disables the portalling
    portal: isPortalled ? undefined : null,
  });

  const sync = createSync(states);
  $effect(() => sync.open(open, (v) => (open = v)));

  // HACK: This is a workaround because melt-ui doesn't properly close
  // menus when other menu triggers are selected.
  // <https://github.com/melt-ui/melt-ui/issues/1196>
  const handleClick = (e: MouseEvent) => {
    if (open) {
      const target = e.target as HTMLElement;

      if (target.closest("button, a") === null) {
        return;
      }

      if (menuElement && menuElement.contains(target)) {
        // Inner click
        if (closeOnInnerClick) {
          closeMenu();
        }
      } else {
        // Outer clicks should close menu if not button or a
        closeMenu();
      }
    }
  };

  // Clicks on another button or link close this menu in the capture phase, so
  // triggers that stop propagation (e.g. another menu's trigger) still do.
  // The menu's own trigger and items are left to `handleClick` and melt-ui.
  const handleOuterClickCapture = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button, a") === null) return;
    if (menuElement?.contains(target) || triggerElement?.contains(target)) {
      return;
    }
    closeMenu();
  };

  let context = $state<DropdownContext>({
    focusedIndex: 0,
    closeMenu,
    lastNavType: null,
  });
  setContext(DROPDOWN_CONTEXT, context);

  function closeMenu() {
    open = false;
    context.lastNavType = null;
  }

  // Add event listener only while the menu is open. Needs to be done in
  // `setTimeout()`, otherwise the trigger closes the menu immediately.
  $effect(() => {
    if (!browser) return;
    if (open) {
      const timeoutId = setTimeout(() => {
        document.body.addEventListener("click", handleClick);
        document.body.addEventListener("click", handleOuterClickCapture, true);
      });

      return () => {
        clearTimeout(timeoutId);
        document.body.removeEventListener("click", handleClick);
        document.body.removeEventListener(
          "click",
          handleOuterClickCapture,
          true,
        );
      };
    }
  });

  $effect(() => {
    // Only set the listener if the dropdown is open.
    if (!open) return;

    // Prevent redundant sets.
    if (context.lastNavType === "mouse") return;

    const listener = () => {
      context.lastNavType = "mouse";
    };
    window.addEventListener("mousemove", listener);
    return () => window.removeEventListener("mousemove", listener);
  });

  $effect(() => {
    if (!open) return;
    if (!elements?.length) return;
    const count = elements.length;

    const keyHandler = (e: KeyboardEvent) => {
      if (!open) {
        return;
      } else if (e.key === "ArrowDown") {
        context.focusedIndex = (context.focusedIndex + 1) % count;
        context.lastNavType = "key";
      } else if (e.key === "ArrowUp") {
        context.focusedIndex = (context.focusedIndex - 1 + count) % count;
        context.lastNavType = "key";
      }
    };

    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  });

  let elementCount = $derived(elements?.length ?? 0);

  // Reset focused index to 0 when the number of elements changes (e.g. filtering)
  $effect(() => {
    elementCount;
    untrack(() => {
      context.focusedIndex = 0;
    });
  });
</script>

<span class="flex" use:melt={$trigger} bind:this={triggerElement}>
  {@render button?.()}
</span>

{#if open}
  <!-- Portalled menus render at the popover layer, above overlays like Drawer. -->
  <div
    use:melt={$menu}
    class="flex flex-col outline-hidden"
    class:z-110={isPortalled}
    bind:this={menuElement}
  >
    <div class="relative z-20 flex min-h-0 flex-col">
      <div
        class="menu-items flex min-h-0 flex-col overflow-hidden overscroll-contain"
        class:w-full={align === "full"}
        style="scrollbar-width: none"
      >
        {#if isFilterable}
          <div class="shrink-0 border-b border-secondary p-2">
            <input
              type="text"
              class="filter-input w-full"
              placeholder={filterPlaceholder || "Search..."}
              bind:value={filterQuery}
            />
          </div>
        {/if}
        <div
          class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
          style:max-height={`${contentMaxHeightPx}px`}
        >
          {@render children({ createSubmenu, closeMenu })}
        </div>
        {#each allFooterSegments as { snippet: segment, raw }}
          {#if raw}
            {@render segment({ createSubmenu, closeMenu })}
          {:else}
            <div class="shrink-0 border-t border-secondary p-2">
              {@render segment({ createSubmenu, closeMenu })}
            </div>
          {/if}
        {/each}
      </div>
    </div>
  </div>
{/if}

<style lang="postcss">
  @reference "$app.css";

  .menu-items {
    @apply rounded-lg border border-secondary bg-overlay;
  }

  .menu-items :global(hr) {
    @apply my-1.5 border-secondary;
  }

  .filter-input {
    @apply rounded-md border border-secondary bg-transparent px-2 py-1 text-sm text-primary outline-none placeholder:text-tertiary;
  }
</style>
